import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Plus, Minus } from 'lucide-react';

const FAQSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const faqs = [
    {
      question: 'What is FoodSply and how does it work?',
      answer: 'FoodSply is a blockchain-powered platform that connects consumers directly with farmers, eliminating middlemen and ensuring fresh, sustainable produce. Our platform uses smart contracts to facilitate transparent transactions and track food from farm to table.',
    },
    {
      question: 'What is the FSPY token used for?',
      answer: 'FSPY tokens serve multiple purposes: governance voting, staking rewards, transaction fee discounts, premium platform access, and incentivizing sustainable farming practices. Token holders actively participate in shaping the platforms future.',
    },
    {
      question: 'How do I connect my wallet and buy FSPY tokens?',
      answer: 'You can connect any Web3 wallet like MetaMask, WalletConnect, or Coinbase Wallet. Once connected, you can swap ETH or other supported tokens for FSPY directly through our platform interface.',
    },
    {
      question: 'Is the platform available globally?',
      answer: 'We are launching in select regions and expanding globally. Currently, we support farmers and consumers in North America and Europe, with plans to expand to Asia and other continents in 2025.',
    },
    {
      question: 'How does FoodSply ensure food quality and safety?',
      answer: 'Our blockchain system tracks every step of the supply chain, from seed to sale. Farmers undergo verification processes, and all transactions are recorded immutably, ensuring transparency and accountability.',
    },
    {
      question: 'What are the benefits for farmers joining FoodSply?',
      answer: 'Farmers benefit from direct access to consumers, higher profit margins by eliminating middlemen, transparent pricing, reduced waste through better demand forecasting, and rewards for sustainable practices.',
    },
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-display font-bold text-gray-900 mb-6">
            Frequently Asked <span className="gradient-text">Questions</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Everything you need to know about FoodSply, our token, and how 
            we're revolutionizing the food supply chain.
          </p>
        </motion.div>

        <div className="max-w-4xl mx-auto space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-gray-50 rounded-2xl overflow-hidden"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-8 py-6 text-left flex items-center justify-between hover:bg-gray-100 transition-colors"
              >
                <h3 className="text-lg font-semibold text-gray-900 pr-4">
                  {faq.question}
                </h3>
                <div className="flex-shrink-0">
                  {openIndex === index ? (
                    <Minus className="w-5 h-5 text-primary-600" />
                  ) : (
                    <Plus className="w-5 h-5 text-primary-600" />
                  )}
                </div>
              </button>
              
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-8 pb-6">
                      <p className="text-gray-600 leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="text-center mt-16"
        >
          <p className="text-gray-600 mb-4">
            Still have questions?
          </p>
          <a
            href="mailto:support@foodsply.com"
            className="text-primary-600 hover:text-primary-700 font-semibold transition-colors"
          >
            Contact our support team
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQSection;